import { loadWorkoutsAction } from '@gymaholic/packages'
import {
  createISOStrDateUtil,
  subDaysDateUtil
} from 'packages/src'
import Button from 'components/Button'
import ButtonPrimary from 'components/ButtonPrimary'
import { LoadingIcon } from 'components/Icon/icons/loading'
import Text from 'components/Text'
import { View } from 'components/View'
import { format } from 'date-fns'
import formatMessageIntlUtil from 'modules/intl/utils/format-message'
import getActionStrReduxUtil from 'modules/redux/utils/get-action-str'
import {
  getWorkoutsLoggedGraphDataSelector,
  isLoadedWorkoutsLooggedSelector,
  isLoadingWorkoutsLoggedSelector
} from 'modules/workout/redux/selectors'
import React from 'react'
import useIntl from 'modules/component/utils/hooks/useIntl'
import useDispatch from 'modules/component/utils/hooks/useDispatch.js'
import useSelector from 'modules/component/utils/hooks/useSelector.js'
import '../victory-chart-scroll-events.css'
import TextTitleCondensed from 'components/TextTitleCondensed.js'
import { logEventAnalyticsUtil } from 'modules/analytics/utils/log-event/index.js'

import REDUX_OPTION from 'modules/redux/constants/option.js'
import { defineMessages } from 'react-intl'

let VictoryAxis, VictoryBar, VictoryChart

const DAYS_DIFF = 8
const dateFormat = 'EEEEE'
const graphTypes = {
  COUNT: 'COUNT',
  VOLUME: 'VOLUME',
  CALORIES: 'CALORIES'
}

const messages = defineMessages({
  workoutsLoggedPerDay: {
    id: 'WorkoutsLoggedGraph.workoutsLoggedPerDay',
    defaultMessage: 'WORKOUTS LOGGED PER DAY'
  },
  caloriesBurned: {
    id: 'WorkoutsLoggedGraph.caloriesBurned',
    defaultMessage: 'Calories burned per day'
  },
  trainingVolume: {
    id: 'WorkoutsLoggedGraph.trainingVolume',
    defaultMessage: 'Training Volume per day'
  },
  seeWorkouts: {
    id: 'WorkoutsLoggedGraph.seeWorkouts',
    defaultMessage: 'See workouts'
  },
  averageDuration: {
    id: 'WorkoutsLoggedGraph.averageDuration',
    defaultMessage: 'Avg Duration'
  },
  averageVolume: {
    id: 'WorkoutsLoggedGraph.averageVolume',
    defaultMessage: 'Avg Volume'
  },
  averageCalories: {
    id: 'WorkoutsLoggedGraph.averageCalories',
    defaultMessage: 'Avg Calories'
  },
  count: {
    id: 'WorkoutsLoggedGraph.Count',
    defaultMessage: 'Count'
  },
  volume: {
    id: 'WorkoutsLoggedGraph.volume',
    defaultMessage: 'Volume'
  },
  calories: {
    id: 'WorkoutsLoggedGraph.calories',
    defaultMessage: 'Calories'
  }
})

export function WorkoutsLoggedGraph ({ handleClickTryWorkout }) {
  const intl = useIntl()
  const dispatch = useDispatch()
  const [graphType, setGraphType] = React.useState(graphTypes.COUNT)
  const [isVictoryLoaded, setIsVictoryLoaded] = React.useState(false)
  const workoutsLoggedGraphData = useSelector(getWorkoutsLoggedGraphDataSelector())
  const isLoadingWorkoutsLogged = useSelector(isLoadingWorkoutsLoggedSelector())
  const isLoadedWorkoutsLogged = useSelector(isLoadedWorkoutsLooggedSelector())
  const dates = workoutsLoggedGraphData?.dates

  const hasNoData = (
    !workoutsLoggedGraphData ||
    workoutsLoggedGraphData?.averageDuration === 0
  )

  const loadWorkouts = () => {
    const today = new Date()
    const maxLoggedAt = createISOStrDateUtil(today)
    const minLoggedAt = createISOStrDateUtil(
      subDaysDateUtil(today, DAYS_DIFF)
    )
    dispatch(
      loadWorkoutsAction({
        query: {
          isLogged: true,
          minLoggedAt,
          maxLoggedAt
        },
        options: {
          [REDUX_OPTION.IGNORE_UPDATED_AT]: true
        }
      })
    )
  }
  const handleMount = () => {
    if (isLoadedWorkoutsLogged) {
      return
    }

    loadWorkouts()
  }

  React.useEffect(() => {
    handleMount()
  }, [])

  React.useEffect(() => {
    const loadVictory = async () => {
      if (!isVictoryLoaded && !hasNoData && !isLoadingWorkoutsLogged) {
        const victory = await import(/* webpackChunkName: "victory" */ 'victory')
        VictoryAxis = victory.VictoryAxis
        VictoryBar = victory.VictoryBar
        VictoryChart = victory.VictoryChart
        setIsVictoryLoaded(true)
      }
    }
    loadVictory()
  }, [isLoadingWorkoutsLogged, hasNoData, isVictoryLoaded])

  const getTitle = () => {
    switch (graphType) {
      case graphTypes.COUNT:
        return formatMessageIntlUtil(intl, messages.workoutsLoggedPerDay)
      case graphTypes.CALORIES:
        return formatMessageIntlUtil(intl, messages.caloriesBurned)
      case graphTypes.VOLUME:
        return formatMessageIntlUtil(intl, messages.trainingVolume)
      default:
        return null
    }
  }

  if (isLoadingWorkoutsLogged) {
    return (
      <View
        mt='16px'
        mx='32px'
        py='16px'
        style={{
          border: '1px solid #DDDDDD',
          borderRadius: '6px'
        }}
      >
        <View
          mx='16px'
          row
          style={{
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Text
            fs='1.125em'
            fw='800'
            pb='8px'
            style={{
              transform: 'scaleX(0.8) scaleY(1.2)',
              transformOrigin: '0 0',
              textTransform: 'uppercase'
            }}
          >
            {getTitle()}
          </Text>
          <LoadingIcon
            stroke='black'
            style={{
              width: '30px',
              height: '30px'
            }}
          />
        </View>
      </View>
    )
  }

  const getDomain = () => {
    switch (graphType) {
      case graphTypes.COUNT:
        return workoutsLoggedGraphData.countDomain
      case graphTypes.CALORIES:
        return workoutsLoggedGraphData.caloriesDomain
      case graphTypes.VOLUME:
        return workoutsLoggedGraphData.volumeDomain
      default:
        return null
    }
  }

  const getTickValues = () => {
    switch (graphType) {
      case graphTypes.COUNT:
        return workoutsLoggedGraphData.countTickValues
      case graphTypes.CALORIES:
        return workoutsLoggedGraphData.caloriesTickValues
      case graphTypes.VOLUME:
        return workoutsLoggedGraphData.volumeTickValues
      default:
        return null
    }
  }

  const getData = () => {
    switch (graphType) {
      case graphTypes.COUNT:
        return workoutsLoggedGraphData.countGraphData
      case graphTypes.CALORIES:
        return workoutsLoggedGraphData.caloriesGraphData
      case graphTypes.VOLUME:
        return workoutsLoggedGraphData.volumeGraphData
      default:
        return null
    }
  }

  const YTicks = ({
    x,
    y,
    datum
  }) => {
    return (
      <text
        x={x - 40}
        y={y}
        style={{
          color: '#263238',
          fontSize: '16px',
          opacity: 0.3,
          fontWeight: '590'
        }}
      >{graphType === graphTypes.VOLUME
        ? datum > 0
            ? `${datum}k`
            : datum
        : datum}
      </text>
    )
  }

  const renderGraph = () => {
    if (!isVictoryLoaded) {
      return null
    }

    const tickFormat = (value) => {
      return graphType === graphTypes.VOLUME
        ? value > 0
            ? `${value}k`
            : value
        : value
    }

    return (
      <VictoryChart
        height={334}
        padding={{
          top: 25,
          bottom: 50,
          left: 50,
          right: 50
        }}
      >
        <VictoryAxis
          dependentAxis
          domain={getDomain()}
          tickValues={getTickValues()}
          tickFormat={tickFormat}
          tickLabelComponent={<YTicks />}
          style={{
            axis: { strokeWidth: 0 },
            tickLabels: {
              fill: '#263238',
              fontSize: '16px',
              fontFamily: 'unset',
              opacity: 0.3,
              fontWeight: '590'
            }
          }}
        />
        <VictoryAxis
          tickFormat={(date) => format(date, dateFormat)}
          tickValues={dates}
          style={{
            axis: { strokeWidth: 0 },
            tickLabels: {
              fill: '#7F8D94',
              fontSize: '16px',
              fontFamily: 'unset',
              fontWeight: '590'
            }
          }}
        />
        <VictoryBar
          cornerRadius={2}
          barWidth={20}
          standalone={false}
          data={getData()}
          style={{
            data: {
              fill: '#151D21'
            }
          }}
        />
      </VictoryChart>
    )
  }

  if (hasNoData) {
    return
  }

  if (hasNoData) {
    return (
      <View
        mt='16px'
        mx='32px'
        py='16px'
        style={{
          border: '1px solid #DDDDDD',
          borderRadius: '6px'
        }}
      >
        <View
          mx='16px'
          row
          style={{
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <TextTitleCondensed>
            {getTitle()}
          </TextTitleCondensed>

          <ButtonPrimary
            style={{
              height: '32px',
              minWidth: '8em'
            }}
            onClick={handleClickTryWorkout}
          >
            <Text
              fs='0.8em'
              fw='600'
              c='#FFFFFF'
              style={{ textTransform: 'uppercase' }}
            >
              {formatMessageIntlUtil(intl, messages.seeWorkouts)}
            </Text>
          </ButtonPrimary>
        </View>
      </View>
    )
  }

  return (
    <View>
      {
        workoutsLoggedGraphData && (
          <View
            mt='16px'
            mx='32px'
            pb='16px'
            style={{
              border: '1px solid #DDDDDD',
              borderRadius: '6px'
            }}
          >
            <View
              mt='16px'
              mx='16px'
              pb='16px'
              style={{ borderBottom: '1px solid #DDDDDD' }}
            >
              <Text
                fs='1.125em'
                fw='800'
                style={{
                  transform: 'scaleX(0.8) scaleY(1.2)',
                  transformOrigin: '0 0',
                  textTransform: 'uppercase'
                }}
              >
                {getTitle()}
              </Text>
              <View
                mt='1.1em'
                row
              >
                <View
                  mr='1.60em'
                >
                  <Text
                    fs='0.6875em'
                    fw='590'
                    c='#151D21'
                  >
                    {formatMessageIntlUtil(intl, messages.averageDuration)}
                  </Text>
                  <Text
                    mt='0.1em'
                    fs='1.125em'
                    fw='800'
                    style={{
                      transform: 'scaleX(0.8) scaleY(1.2)',
                      transformOrigin: '0 0',
                      textTransform: 'uppercase'
                    }}
                  >
                    {workoutsLoggedGraphData.averageDuration}{' '}
                    <span style={{ fontSize: '0.75em' }}>
                      M
                    </span>
                  </Text>
                </View>
                <View
                  mr='1.60em'
                >
                  <Text
                    fs='0.6875em'
                    fw='590'
                    c='#151D21'
                  >
                    {formatMessageIntlUtil(intl, messages.averageVolume)}
                  </Text>
                  <Text
                    mt='0.1em'
                    fs='1.125em'
                    fw='800'
                    style={{
                      transform: 'scaleX(0.8) scaleY(1.2)',
                      transformOrigin: '0 0',
                      textTransform: 'uppercase'
                    }}
                  >
                    {workoutsLoggedGraphData.volumeAverage}{' '}
                    <span style={{ fontSize: '0.75em' }}>
                      K
                    </span>
                  </Text>
                </View>
                <View>
                  <Text
                    fs='0.6875em'
                    fw='590'
                    c='#151D21'
                  >
                    {formatMessageIntlUtil(intl, messages.averageCalories)}
                  </Text>
                  <Text
                    mt='0.1em'
                    fs='1.125em'
                    fw='800'
                    style={{
                      transform: 'scaleX(0.8) scaleY(1.2)',
                      transformOrigin: '0 0',
                      textTransform: 'uppercase'
                    }}
                  >
                    {workoutsLoggedGraphData.caloriesAverage}{' '}
                    <span style={{ fontSize: '0.75em' }}>
                      kcal
                    </span>
                  </Text>
                </View>
              </View>
            </View>
            <View
              pl='19px'
            >
              {renderGraph()}
            </View>
            <View
              px='16px'
              row
              style={{
                justifyContent: 'space-between',
                gap: '4px'
              }}
            >
              <GraphBtn
                text={formatMessageIntlUtil(intl, messages.count)}
                isSelected={graphType === graphTypes.COUNT}
                onClick={() => setGraphType(graphTypes.COUNT)}
              />
              <GraphBtn
                text={formatMessageIntlUtil(intl, messages.volume)}
                isSelected={graphType === graphTypes.VOLUME}
                onClick={() => setGraphType(graphTypes.VOLUME)}
              />
              <GraphBtn
                text={formatMessageIntlUtil(intl, messages.calories)}
                isSelected={graphType === graphTypes.CALORIES}
                onClick={() => setGraphType(graphTypes.CALORIES)}
              />
            </View>
          </View>
        )
      }
    </View>
  )
}

function GraphBtn ({
  text,
  isSelected,
  onClick
}) {
  const ANALYTIC_EVENT = {
    WORKOUTS_LOGGED_GRAPH: 'WORKOUTS_LOGGED_GRAPH'
  }

  const handleClick = () => {
    logEventAnalyticsUtil(getActionStrReduxUtil(ANALYTIC_EVENT.WORKOUTS_LOGGED_GRAPH, text))
    onClick()
  }

  return (
    <Button
      onClick={handleClick}
      isFullWidth
      style={{
        width: '100%',
        height: '1.575em',
        background: isSelected
          ? '#151D21'
          : '#FFFFFF',
        flexGrow: 1,
        borderRadius: '6px',
        justifyContent: 'center',
        border: isSelected
          ? 'none'
          : '1px solid #7F8D94'
      }}
    >
      <Text
        c={isSelected ? '#FFFFFF' : '#7F8D94'}
        fs='0.6875em'
        fw='590'
      >
        {text}
      </Text>
    </Button>
  )
}
